/**
 * Guide data for the web (Sep 2026): which docs this user sees, the ask
 * callable, and the what's-new "seen" marker. Docs are compiled into
 * @daaily/shared from shared/guide/*.md.
 *
 * Visibility on the web follows the REAL nav (useAppNav): a doc shows when its
 * route is one the user can open, plus the `all` docs. So the Guide lists
 * exactly the apps in the user's sidebar, no more.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { httpsCallable } from 'firebase/functions';
import { CALLABLES, GUIDE_DOCS, WHATS_NEW, type GuideAskRequest, type GuideAskResponse, type GuideDoc, type WhatsNewEntry } from '@daaily/shared';
import { functions } from '../lib/firebase';
import { useSharedAppNav } from '../components/useAppNav';

export const callGuideAsk = httpsCallable<GuideAskRequest, GuideAskResponse>(functions, CALLABLES.GUIDE_ASK);

export function useVisibleGuideDocs(): GuideDoc[] {
  const { navGroups, adminItems, isManager } = useSharedAppNav();
  return useMemo(() => {
    const routes = new Set<string>([
      ...navGroups.flatMap((g) => g.items.map((i) => i.to)),
      ...adminItems.map((i) => i.to),
      ...(isManager ? ['/my-team'] : []),
    ]);
    return GUIDE_DOCS.filter((d) => d.access === 'all' || routes.has(d.route));
  }, [navGroups, adminItems, isManager]);
}

export function useVisibleWhatsNew(docs: GuideDoc[]): WhatsNewEntry[] {
  return useMemo(() => {
    const ids = new Set(docs.map((d) => d.id).concat('hub'));
    return WHATS_NEW.filter((e) => ids.has(e.docId));
  }, [docs]);
}

// ── What's new "seen" marker (per browser): the newest entry date the user has seen ──

const WN_KEY = 'daaily.guide.whatsNewSeen';
const wnListeners = new Set<() => void>();
function readSeen(): string {
  try { return localStorage.getItem(WN_KEY) ?? ''; } catch { return ''; }
}
let wnSeen = readSeen();
export function markWhatsNewSeen(latest: string) {
  if (!latest || latest <= wnSeen) return;
  wnSeen = latest;
  try { localStorage.setItem(WN_KEY, latest); } catch { /* ignore */ }
  for (const l of wnListeners) l();
}
export function useWhatsNewSeen(): string {
  return useSyncExternalStore((l) => { wnListeners.add(l); return () => wnListeners.delete(l); }, () => wnSeen);
}

/** "3 days ago"-style label for an ISO date. */
export function dateLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
